import React from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';
import Chart from 'react-apexcharts';
import PropTypes from 'prop-types';
import { Card, CardContent, Chip, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import { TitleTooltip } from './TitleTooltip';
import { LoadingView } from './LoadingView';
import { ServiceLineSelect } from './ServiceLineSelect';
import { SmallIconTextButton } from './SmallIconTextButton';

import { ServiceLineUtil, StringUtil, withIsMobile } from 'utils';
import {
  StoreUtil,
  PatientTypes,
  PatientTypeDisplay,
  LocationTypeUtil,
  CsvUtil,
} from 'doctivity-shared/utils';
import fileDownload from 'js-file-download';
import { format } from 'date-fns';

import { FindOrganizationDialog } from 'organizations';

import { showDialog, hideDialog } from 'store/actions/systemActions';

import { listClaimsByOrganization } from 'store/actions/claimsActions';
import { PatientTypeSelect } from './PatientTypeSelect';

const LIMIT_SERVICELINES = 25;
const CARD_TITLE = 'Patients by Service Line';
const CARD_FILE_LABEL = 'PATIENTS_BY_SERVICE_LINE';

// CsvUtil.matrixToString joins on commas with no quoting; org and service line
// names can contain commas/quotes, so escape each cell before joining.
const csvEscape = (v) => {
  const str = v == null ? '' : String(v);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

// Strip characters that aren't allowed in file names on common OSes.
const safeFilePart = (v) =>
  String(v ?? '').replace(/[\\/:*?"<>|]+/g, '-').trim();

const styles = (theme) => ({
  filters: {
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  filter: {
    marginRight: theme.spacing(),
    minWidth: 168,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
  },
  noData: {
    textAlign: 'center',
    paddingTop: 50,
    minHeight: 200,
  },
  lastFilter: {
    marginBottom: 0,
  },
  leftIcon: {
    marginRight: theme.spacing(),
  },
  back: {
    marginTop: theme.spacing(2),
  },
  nothingFound: {
    fontWeight: 300,
    fontSize: 12,
  },
  
  colorKeyContainer: {
    minHeight: 40,
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  colorKey: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'top',
    flexWrap: 'wrap',
    fontSize: 12,
    fontWeight: 400,
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
    },
  },
  colorKeyIcon: {
    width: 14,
    height: 14,
    marginRight: 2,
    borderRadius: 2,
  },
  colorKeyText: {
    marginRight: 12,
    whiteSpace: 'nowrap',
  },
  aggregatePercentage: {
    marginLeft: theme.spacing(1),
  },
  colorPagination: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    }
    }
  
  
});

/*
const series = [
  {
    name: 'Ambulatory Surgery Center',
    data: [36, 0, 0, 0, 0, 0, 36],
  },
  {
    name: 'Outpatient',
    data: [0, 0, 50, 15, 13, 0, 94],
  },
  {
    name: 'Inpatient',
    data: [0, 28, 35, 0, 0, 58, 131],
  },
  {
    name: 'Office',
    data: [0, 0, 0, 11, 0, 197, 197],
  },
];

const categories = [
  'Foot',
  'Hip Revision Replacement',
  'Knee Revision Replacement',
  'General Medical Orthopedics',
  'Sports Medicine',
  'General Surgical Orthopedics',
  'Orthopedics',
];
*/
class ClaimsByServiceline extends React.Component {
  constructor(props) {
    super(props);

    this.cardElement = React.createRef(null);

    this.state = {
      compareOrg: null,
      serviceline: 'all',
    };
  }

  fetchCompareOrgData(org) {
    this.props.dispatch(
      listClaimsByOrganization(org.id, this.props.patientType)
    );
    this.setState({ compareOrg: org });
  }

  // Builds the categories (rows) and stacked series (care settings) exactly as the
  // chart displays them. Shared by render() and the CSV export so the download can
  // never drift from what the user sees (top-level vs. sub service lines, compare org,
  // patient type, '< 11' suppression).
  buildChartData() {
    const { claims: storeClaims, app } = this.props;

    const allClaims = storeClaims?.claims ?? storeClaims?.data ?? [];
    const claims = allClaims.filter(c => 
      !c.serviceline?.client_id || c.serviceline.client_id === app.selectedClient
    );

    const { serviceline } = this.state;
    let isLoading = StoreUtil.isLoading(storeClaims);

    let compareByServiceline = null;
    if (this.state.compareOrg) {
      const compareClaims = StoreUtil.get(
        this.props.claimsState,
        'claims_by_organization',
        parseInt(this.state.compareOrg.id, 10)
      );

      if (!StoreUtil.isLoaded(compareClaims)) {
        isLoading = true
      }

      compareByServiceline = !isLoading && this.groupByServiceline(
        StoreUtil.getData(compareClaims),
        false
      );
    }

    const categories = [];
    let series = [];
    let colors = [];

    LocationTypeUtil.getAll()
        .filter((type) => !type.codesOnly && type.id !== 0)
        .map((type) => {
          series.push({
            name: type.name,
            data: [],
            unknown: []
          })          
          colors.push(type.color);
        })

    if (this.props.patientType === 'CAPTURED_CLAIMS') {
      series = [{ name: 'Claims', data: [], unknown: [] }];
    }

    const byServiceline = this.groupByServiceline(claims);

    Object.keys(byServiceline).forEach((sl) => {
      const servicelineOuter = byServiceline[sl];
      const servicelineObjects = Object.values(servicelineOuter);
      let serviceline = null;
      if (servicelineObjects.length > 0) {
        serviceline = servicelineObjects[0].serviceline;
      }
      let isParent = false;
      if (serviceline) {
        isParent = serviceline.parent_id === null;
      }
      categories.push({ label: sl, isParent, servicelineId: serviceline?.id });
      this.addToSeries(series, byServiceline, sl);

      if (compareByServiceline) {
        categories.push({ label: sl, compare: true });
        this.addToSeries(series, compareByServiceline, sl);
      }
    });

    const topLevelServicelines = this.groupByServiceline(claims, false, true);
    const showSpecificServicelineIds =
      serviceline === 'all' ? [] : [serviceline];
    Object.keys(topLevelServicelines).forEach((sl) => {
      const servicelineOuter = topLevelServicelines[sl];
      const servicelineObjects = Object.values(servicelineOuter);
      let serviceline = null;
      if (servicelineObjects.length > 0) {
        serviceline = servicelineObjects[0].serviceline;
      }
      this.calculateShowTopLevel(
        series,
        topLevelServicelines,
        sl,
        serviceline?.id,
        showSpecificServicelineIds
      );
    });

    return {
      categories,
      series,
      colors,
      showSpecificServicelineIds,
      isLoading,
    };
  }

  render() {
    const { classes, isProvider, servicelines } = this.props;
    const { serviceline } = this.state;
    const chartData = this.buildChartData();
    const {
      categories,
      series,
      colors,
      showSpecificServicelineIds,
    } = chartData;
    let { isLoading } = chartData;

    const rowCount = categories.length;

    const hasChildren =
      serviceline === 'all'
        ? true
        : ServiceLineUtil.hasChildren(servicelines, serviceline);

    // Create a "fake" version of the series data to show < 10 labels
    const modifiedSeriesData = series.map((innerSeries) => {
      return {
        name: innerSeries.name,
        data: innerSeries.data.map((value, index) => {
          let total = series.reduce((sum, rSeries) => sum + rSeries.data[index], 0);

          return {
            x: innerSeries.name,
            y: value,
            stackTotal: total,
            isUnknown: innerSeries.unknown.filter((v) => v === index).length > 0
          }
        })
      }
    });
    isLoading = isLoading || !this.props.claimsState || 
      StoreUtil.isLoading(this.props.claimsState?.claims_list ?? {}) ||
      StoreUtil.isLoading(this.props.claimsState?.payers_list ?? {}) || 
      StoreUtil.isLoading(this.props.claimsState?.servicelines_list ?? {}) || 
      StoreUtil.isLoading(this.props.claimsState?.servicelines_grouped ?? {});

    // CSV download: only offered once the chart has rows, and always exports exactly
    // the rows the chart is showing (top-level OR sub service lines, never both).
    const canExport = !isLoading && rowCount > 0;
    const exportProps = canExport
      ? {
          onExport: this.onExport,
          csvExportOptions: {
            exportType: 'CLAIMS_BY_SERVICELINE',
            totalCount: rowCount,
            fileName: this.getExportFileName(),
          },
        }
      : {};
    return (
        <Card ref={this.cardElement}>
          <CardContent>
            {isProvider ? (
              <TitleTooltip
                title={CARD_TITLE}
                tooltip='Displaying number of claims or projected patients by selected Service Lines.'
                {...exportProps}
              />
            ) : (
              <TitleTooltip
                title={CARD_TITLE}
                tooltip='Displaying number of claims or projected patients by selected Service Lines.'
                actionIcon='compare_arrows'
                actionText='compare'
                onAction={this.onCompare}
                {...exportProps}
              />
            )}
            <div className={classes.filters}>
              <div className={classes.filter}>
                <ServiceLineSelect
                  hideChildlessTopLevels
                  showSpecificServicelineIds={
                    serviceline === 'all'
                      ? showSpecificServicelineIds
                      : undefined
                  }
                  showAll
                  value={serviceline}
                  onChange={this.onServicelineChange}
                />
              </div>
              <div className={clsx(classes.filter, classes.lastFilter)}>
                <PatientTypeSelect />
              </div>
            </div>
            {serviceline !== 'all' && (
              <div className={classes.back}>
                <SmallIconTextButton
                  icon='chevron_left'
                  text='Top Level Service Lines'
                  onClick={() => this.onServicelineChange('all')}
                />
              </div>
            )}

            {this.state.compareOrg && (
              <Chip
                label={`Comparing ${this.state.compareOrg.name}`}
                onDelete={() => {
                  this.onCompareOrganization(null);
                }}
                variant='outlined'
              />
            )}
            {isLoading && <LoadingView />}
            {!isLoading && rowCount === 0 && hasChildren && (
              <Typography className={classes.noData}>
                No shared patients.
              </Typography>
            )}
            {!isLoading && rowCount === 0 && !hasChildren && (
              <Typography className={classes.noData}>
                No Sub-Service Lines available for this Service Line
              </Typography>
            )}
            {!isLoading && rowCount > 0 && (
              <Chart
                type='bar'
                height={Math.max(50 + rowCount * 26, 180)}
                options={{
                  colors:colors,
                  chart: {
                    id: 'ClaimsByServiceline',
                    toolbar: {
                      show: false,
                    },
                      animations: {
                      enabled: false,
                    },
                    events: {
                      click: this.onChartClick,
                    },
                    stacked: true,
                  },
                  yaxis: {
                    labels: {
                      formatter: (obj) => {
                        return (obj.compare ? ' ' : obj.label);
                      },
                      minWidth: 100,
                    },
                  },
                  xaxis: {
                    categories,
                    labels: {
                      hideOverlappingLabels: true,
                      rotate: -45,
                      formatter: (val) => {
                        return StringUtil.numberForChart(val);
                      },
                    },
                    tickPlacement: 'on',
                  },
                  tooltip: {
                    enabled: true,
                    shared: true,
                    intersect: false,
                    x: {
                      formatter: (val, opts) => {
                        const category = categories[opts.dataPointIndex];
                        if (!category) return val;
                        
                        if (this.state.compareOrg) {
                          if (category.compare) {
                            return `${this.state.compareOrg.name}<br />${category.label}`;
                          }
                          return `${this.props.name}<br />${category.label}`;
                        }
                        return category.label;
                      },
                    },
                    y: {
                      formatter: (val, opts) => {
                        let isUnknown = modifiedSeriesData[opts.seriesIndex].data[opts.dataPointIndex].isUnknown;
                        if (isUnknown) {
                          return '< 11';
                        }
                        let originalValue = series[opts.seriesIndex].data[opts.dataPointIndex];
                        return StringUtil.numberForChart(originalValue);
                      }
                    },
                  },
                  plotOptions: {
                    bar: {
                      horizontal: true
                    },
                  },
                  legend: {
                    show: false,
                  },
                  dataLabels: {
                    enabled: true,
                    dropShadow: {
                      enabled: true,
                      left: 0,
                      top: 0,
                      opacity: 0.4,
                    },
                    formatter: (val, opts) => {
                      let isUnknown = modifiedSeriesData[opts.seriesIndex]?.data[opts.dataPointIndex].isUnknown;
                      if (isUnknown) {
                        return '< 11';
                      }

                      let originalValue = series[opts.seriesIndex]?.data[opts.dataPointIndex];
                      return StringUtil.numberForChart(originalValue);
                    },
                    offsetY: 0,
                  },
                }}
                series={modifiedSeriesData}                
              />
            )}
          </CardContent>
          {!isLoading && rowCount > 0 && (
            <div className={classes.colorPagination}>
            <div className={`${classes.colorKey} ${classes.colorKeyContainer}`}>
              {LocationTypeUtil.getAll()
                  .filter((type) => !type.codesOnly && type.id !== 0)
                  .map((type) => {
                    return (
                      <div className={classes.colorKey} key={type.id}>
                        <div
                          className={classes.colorKeyIcon}
                          style={{
                            backgroundColor: type.color,
                          }}
                        />
                        <div className={classes.colorKeyText}>{type.name}</div>
                      </div>
                    );
                  })}
            </div>
          </div>)}
        </Card>
        
    );
  }

  groupByServiceline(claims, limitCount = false, forceTopLevel = false) {
    const { serviceline } = this.state;
    const byServiceline = {};
    let count = 0;
    claims?.forEach((c) => {
      if (
        ((serviceline === 'all' || forceTopLevel) &&
          c.serviceline.parent_id === null) ||
        (!forceTopLevel && c.serviceline.parent_id === serviceline)
      ) {
        let name = c.serviceline.abbreviation ?? c.serviceline.name;
        let d = byServiceline[name];
        if (!d && (!limitCount || count < LIMIT_SERVICELINES)) {
          count++;
          d = {};
          byServiceline[name] = d;
        }
        if (d) {
          d[c.location_type || 1] = c;
        }
      }
    });
    return byServiceline;
  }

  addToSeries(series, byServiceline, sl) {
    let totalField = 'total_patients';
    if (this.props.patientType === 'CAPTURED_CLAIMS') {
      totalField = 'total_claims';
    }

    for (let i = 0; i < series.length; i++) {
      let total = 0;
      const claims = byServiceline[sl];
      var x = i;

      // fix for Urgent Care only (location_type=10)
      if (i==5) {
        x = 9;
      }

      if (claims?.[x + 1]) {
        // Track the indexes that will need a < 11 label
        if (claims[x+1][totalField] === null) {
          total = 10;
          series[i].unknown.push(series[i].data.length);
        } else {
          total = claims[x + 1][totalField] || 0;
        }
      }

      series[i].data.push(total);
    }
  }
  
  calculateShowTopLevel(
    series,
    byServiceline,
    sl,
    servicelineId,
    showSpecificServicelineIds
  ) {
    let totalField = 'total_patients';
    if (this.props.patientType === 'CAPTURED_CLAIMS') {
      totalField = 'total_claims';
    }
    let cumulativeTotal = 0;
    for (let i = 0; i < series.length; i++) {
      let total = 0;
      const claims = byServiceline[sl];
      if (claims?.[i + 1]) {
        total = claims[i + 1][totalField] || 0;
      }

      cumulativeTotal += total;
    }

    if (cumulativeTotal && showSpecificServicelineIds && servicelineId) {
      showSpecificServicelineIds.push(servicelineId);
    }
  }

  onCompare = () => {
    this.props.dispatch(
      showDialog(
        <FindOrganizationDialog
          id='org-compare-serviceline'
          onSelect={this.onCompareOrganization}
          open
        />
      )
    );
  };

  onCompareOrganization = (org) => {
    if (org) {
      this.fetchCompareOrgData(org);
    } else if (this.state.compareOrg) {
      this.setState({ compareOrg: null });
    }
    this.props.dispatch(hideDialog('org-compare-serviceline'));
  };

  onChartClick = (event, chartContext, config) => {
    const { isMobile } = this.props;
    if (!isMobile && config.dataPointIndex > -1) {
      const selectedServiceline =
        config.config.xaxis.categories[config.dataPointIndex];
      if (selectedServiceline.isParent) {
        this.setState({
          serviceline: selectedServiceline.servicelineId,
        });
      }
    }
  };

  onServicelineChange = (id) => {
    this.setState({
      serviceline: id,
    });
  };

  // The drilled-into parent service line, or null when showing top-level lines.
  getSelectedServiceline() {
    const { serviceline } = this.state;
    if (serviceline === 'all') {
      return null;
    }
    const { servicelines } = this.props;
    return Array.isArray(servicelines)
      ? ServiceLineUtil.findServiceLine(servicelines, serviceline)
      : null;
  }

  getServicelineLabel(sl) {
    return sl ? sl.abbreviation ?? sl.name : null;
  }

  // Default (editable) name: entity + card name [+ drilled service line]
  // [+ compare org] + date, e.g.
  // doctivity_Acme Hospital_PATIENTS_BY_SERVICE_LINE_Orthopedics_26_09_23.csv
  // doctivity_Doe_Jane_PATIENTS_BY_SERVICE_LINE_26_09_23.csv (providers: Last_First)
  getExportFileName() {
    const parts = ['doctivity'];
    const entityLabel = this.props.fileNameLabel || this.props.name;
    if (entityLabel) {
      parts.push(safeFilePart(entityLabel));
    }
    parts.push(CARD_FILE_LABEL);
    const slLabel = this.getServicelineLabel(this.getSelectedServiceline());
    if (slLabel) {
      parts.push(safeFilePart(slLabel));
    }
    if (this.state.compareOrg?.name) {
      parts.push(`vs_${safeFilePart(this.state.compareOrg.name)}`);
    }
    parts.push(format(new Date(), 'yy_MM_dd'));
    return `${parts.join('_')}.csv`;
  }

  onExport = ({ limit, offset = 0, fileName } = {}) => {
    const { name, npi, patientType, isProvider } = this.props;
    const { compareOrg } = this.state;
    const { categories, series } = this.buildChartData();

    // Filters applied to the chart, written on every row so the file is
    // self-describing once it leaves the app.
    const selectedSl = this.getSelectedServiceline();
    const servicelineFilter = selectedSl
      ? this.getServicelineLabel(selectedSl)
      : 'All (Top Level Service Lines)';
    const patientTypeLabel = PatientTypeDisplay[patientType] ?? patientType ?? '';

    // Care settings are stacked in the chart; each becomes its own column.
    // (Captured Claims has a single 'Claims' series.)
    const header = [
      isProvider ? 'Provider' : 'Organization',
      'NPI',
      'Patient Type',
      'Service Line Filter',
      selectedSl ? 'Sub-Service Line' : 'Service Line',
      ...series.map((s) => s.name),
    ];

    const rows = categories.map((category, index) => [
      category.compare ? compareOrg?.name : name,
      category.compare ? compareOrg?.npi : npi,
      patientTypeLabel,
      servicelineFilter,
      category.label,
      // Suppressed cells (< 11) are left BLANK, matching our other downloads: the
      // row's presence says there is data, but we never write '<11' or an estimate
      // (the chart's placeholder value of 10 must not leak into the file).
      // 0 means the care setting has no patients for this service line.
      ...series.map((s) => (s.unknown.includes(index) ? '' : s.data[index] ?? 0)),
    ]);

    const exportRows =
      typeof limit === 'number' ? rows.slice(offset, offset + limit) : rows;

    const matrix = [header, ...exportRows].map((row) => row.map(csvEscape));
    fileDownload(
      CsvUtil.matrixToString(matrix),
      fileName || this.getExportFileName(),
    );
  };
}

ClaimsByServiceline.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
  classes: PropTypes.object,
  claims: PropTypes.object,
  claimsState: PropTypes.object,
  isProvider: PropTypes.bool,
  name: PropTypes.string,
  // Entity NPI, written on each row of the CSV export.
  npi: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Entity label for the default export file name (defaults to `name`).
  fileNameLabel: PropTypes.string,
  servicelines: PropTypes.array,
  patientType: PropTypes.oneOf(PatientTypes),
};

function mapStateToProps(state) {
  const { app } = state;

  return {
    app,
    claimsState: state.claims,
    servicelines: state.claims && state.claims.servicelines_grouped,
    patientType: state.app.patientType,
  };
}

const styled = withIsMobile(withStyles(styles)(ClaimsByServiceline));
const connected = connect(mapStateToProps)(styled);
export { connected as ClaimsByServiceline };
